// packages/engine/src/process-walker.ts
//
// The render walker — assembles a WalkResult (linear steps + structural tree +
// group ranges + reentries + diagnostics) from the SHARED IR step-emitter
// (process-step-emitter.ts). ONE traversal of the resolved process IR feeds both
// the graph (edges) and this walker, so the dense stepIndex can never drift
// between them (the lock-step bug class IR convergence kills; plan 2026-06-29
// Stage 7). The emitter owns order / stepIndex / branch selection / do-inlining /
// nodeIds / ranges; this consumer owns the WalkNode tree shape + lane modifiers.
//
// No DOM, no React, no IO.

import type { QualifiedName } from "./types.js";
import type {
  CompensationFrame,
  NodeId,
  Reentry,
  StepModifiers,
  WalkBranch,
  WalkNode,
  WalkResult,
  WalkStep,
  WalkerContext,
  WalkerDiagnostic,
  WalkerState,
} from "./process-walker-types.js";
import { isAwaitNodeId } from "./process-walker-types.js";
import type {
  ProcessModel,
  SubprocessModel,
  CallNode,
  NoteNode,
  ProseNode,
  AwaitNode,
  ReentryNode,
  SpliceNode,
  LoopNode,
  RetryNode,
  SelectNode,
  ParallelNode,
  ReversibleNode,
} from "./process-model.js";
import {
  emitSteps,
  type EmittedStep,
  type EmitContainer,
  type EmitGroup,
  type EmitVisitor,
} from "./process-step-emitter.js";
import {
  DEFAULT_MODS,
  applyDist,
  groupDelta,
  buildLeafStep,
  resolveReentries,
} from "./process-step-build.js";

export interface WalkOpts {
  /**
   * When true, walker emits children for ALL branches of if/try, not just the
   * selected one — the Flow view keeps conditional alternatives visible (and it
   * is the canonical step numbering the graph shares). Each step still carries
   * its enclosing branch label + selected flag in `modifiers`.
   */
  readonly showAllBranches?: boolean;
}

export function walkProcess(
  process: ProcessModel,
  state: WalkerState,
  ctx: WalkerContext,
  opts: WalkOpts = {},
): WalkResult {
  const steps: WalkStep[] = [];
  const diagnostics: WalkerDiagnostic[] = [];
  const collapsedGroups = new Map<NodeId, { startIndex: number; endIndex: number }>();
  const parallelGroups = new Map<NodeId, { startIndex: number; endIndex: number }>();
  const reentries: Reentry[] = [];
  const compensation: CompensationFrame[] = [];
  // Owner-reassign resolver: stepIndex → HOP stepId key, filled for every call
  // hop with an authored caller span (source-rewritable owner). See WalkResult.
  const reassignTargets = new Map<number, string>();
  // Walk NodeId → IR node stepId key (the `mutateProcessStep` address). The walk
  // NodeId scheme (`call:`/`branch:`) diverges from the IR stepId scheme
  // (`simple:`/`if:`), and only here do both ids coexist — so the WYSIWYG
  // step-edit surfaces resolve a clicked node's IR key through this map rather
  // than string-mangling the walk NodeId. Filled for every step + container.
  const stepKeyByNodeId = new Map<NodeId, string>();
  // First-wins `as` registry for in-walk backward `go` resolution (matches the
  // old `.find`); the forward pass below fixes forward-referenced gos.
  const asByName = new Map<string, WalkStep>();

  const rootId = `p:${process.id ?? process.name}`;

  // SAX stack: GroupFrame collects a body's WalkNodes; ContainerFrame collects
  // its bodies as completed groups. Each frame carries the StepModifiers in
  // scope. Steps always arrive inside a GroupFrame (the emitter wraps every body
  // — including the root — in a group).
  type GroupFrame = {
    type: "group";
    role: EmitGroup["role"];
    label?: string;
    selected: boolean;
    itemIndex?: number;
    children: WalkNode[];
    mods: StepModifiers;
  };
  type GroupData = { role: EmitGroup["role"]; label?: string; selected: boolean; itemIndex?: number; children: WalkNode[] };
  type ContainerFrame = {
    type: "container";
    c: EmitContainer;
    groups: GroupData[];
    mods: StepModifiers;
  };
  const stack: (GroupFrame | ContainerFrame)[] = [];
  let rootChildren: WalkNode[] = [];

  const topGroup = (): GroupFrame => {
    const t = stack[stack.length - 1];
    return t as GroupFrame; // a step/terminator/built-node always lands in a group
  };

  const addNode = (n: WalkNode): void => {
    topGroup().children.push(n);
  };

  const branch = (x: { label?: string; selected: boolean; children: WalkNode[] }, fallbackLabel: string): WalkBranch => ({
    label: x.label ?? fallbackLabel,
    selected: x.selected,
    children: x.children,
  });

  const buildContainer = (c: EmitContainer, groups: ContainerFrame["groups"]): WalkNode[] => {
    const nodeId = c.nodeId;
    // Map the container's walk NodeId to its IR node stepId key (see above).
    const cStepId = (c.node as { stepId?: { path: readonly string[] } }).stepId;
    if (cStepId) stepKeyByNodeId.set(nodeId, cStepId.path.join("/"));
    // Owner prefix of the construct (`A if …`, `B parallel …`) — resolved
    // performer path, undefined for dist/ownerless. Views colour controls by it.
    const owner = (c.node as { owner?: { resolved?: import("./types.js").QualifiedName } }).owner?.resolved;
    const ownerSpread = owner ? { owner } : {};
    const one = (role: EmitGroup["role"]) => groups.find((x) => x.role === role);
    const many = (role: EmitGroup["role"]) => groups.filter((x) => x.role === role);
    switch (c.irKind) {
      case "loop": {
        const h = (c.node as LoopNode).head;
        return [{ kind: "each", nodeId, ...ownerSpread, label: h?.label?.text, source: h?.collection?.text, children: one("body")?.children ?? [] }];
      }
      case "retry": {
        const h = (c.node as RetryNode).bound;
        const tryNode: WalkNode = {
          kind: "try",
          nodeId: `${nodeId}/retry`,
          ...ownerSpread,
          tryBranch: { label: "try", selected: true, children: one("attempt")?.children ?? [] },
          catches: many("catch").map((x) => branch({ ...x, selected: true }, "catch")),
        };
        // The `else` (bound-exhausted exit, PROCESS.md §4) runs AFTER the loop
        // gives up — it is not iterated. Emit it as a SIBLING after the retry
        // loop, not a child, so it sits outside the loop frame / loop-back rail
        // (the enclosing-frame stamp comes from the tree, so this places the else
        // outside the loop in flow AND BPMN). The loop body is the attempt only.
        const elseChildren = one("elseBody")?.children ?? [];
        return [
          {
            kind: "each", nodeId, ...ownerSpread, label: h?.label?.text, source: h?.collection?.text,
            retry: true, children: [tryNode],
          },
          ...elseChildren,
        ];
      }
      case "branch": {
        const elseG = one("else");
        return [{
          kind: "if", nodeId, ...ownerSpread,
          branches: many("arm").map((x) => branch(x, "")),
          ...(elseG ? { elseBranch: branch(elseG, "else") } : {}),
        }];
      }
      case "select": {
        const sn = c.node as SelectNode;
        const runCount = sn.count.kind === "all" ? undefined : sn.count.kind === "one" ? 1 : sn.count.n;
        return [{ kind: "switch", nodeId, ...ownerSpread, head: sn.head?.text, cases: many("case").map((x) => branch({ ...x, selected: true }, "")), inclusive: true, runCount }];
      }
      case "guard":
        return [{
          kind: "try", nodeId, ...ownerSpread,
          tryBranch: branch(one("attempt") ?? { selected: true, children: [] }, "try"),
          catches: many("catch").map((x) => branch(x, "catch")),
        }];
      case "parallel": {
        if (c.endIndex !== undefined && c.endIndex >= c.startIndex) parallelGroups.set(nodeId, { startIndex: c.startIndex, endIndex: c.endIndex });
        const fan = one("fanout");
        const branches: WalkBranch[] = fan
          ? [{ label: "each", selected: true, children: fan.children }]
          : many("branch").map((x, bi) => ({ label: `branch:${bi}`, selected: true, children: x.children }));
        return [{ kind: "parallel", nodeId, ...ownerSpread, branches, mergeAspect: parallelMergeAspect(c.node as ParallelNode) }];
      }
      case "splice": {
        const sn = c.node as SpliceNode;
        const expanded = c.expanded ?? false;
        if (!expanded && c.endIndex !== undefined && c.endIndex >= c.startIndex) collapsedGroups.set(nodeId, { startIndex: c.startIndex, endIndex: c.endIndex });
        const body = expanded ? one("body")?.children ?? [] : undefined;
        return [{
          kind: "do", nodeId, target: sn.target.join("."), args: sn.args?.map((a) => a.text),
          ...(sn.comment ? { comment: sn.comment.text } : {}),
          expanded, ...(body ? { body } : {}), ...(c.recursive ? { recursive: true } : {}),
        }];
      }
      case "owner_block":
        return one("body")?.children ?? []; // transparent
      case "reversible": {
        // Transparent in the tree (forwards then reverse unwinds, per the
        // emitter), PLUS the backward compensation structure surfaced as a side-
        // channel and a synthesized default `fail` terminus (PROCESS.md §8).
        const revId = nodeId;
        const items = (c.node as ReversibleNode).items;
        const fwdEntry = new Map<number, NodeId>();
        const unwEntry = new Map<number, NodeId>();
        const unwindOrder: number[] = []; // emission order = reverse item order
        for (const g of groups) {
          if (g.itemIndex == null) continue;
          const first = g.children[0];
          if (!first) continue;
          const id = first.nodeId;
          if (g.role === "item") {
            // §2.6: a prose item is not an action — it cannot fail, so it
            // gets no compensation failure-drop edge.
            if (!(first.kind === "step" && first.prose)) fwdEntry.set(g.itemIndex, id);
          }
          else if (g.role === "unwind") { unwEntry.set(g.itemIndex, id); unwindOrder.push(g.itemIndex); }
        }
        const children: WalkNode[] = groups.flatMap((x) => x.children);
        // The chain's tail is the last-emitted unwind (smallest item index). If it
        // is itself an `unwind fail`, it already terminates the chain; otherwise
        // append the synthesized default failure outcome.
        const tailItem = unwindOrder.length ? unwindOrder[unwindOrder.length - 1]! : undefined;
        const tailIsTerminator = tailItem != null && items[tailItem]?.unwind?.action.kind === "terminator";
        const failNodeId = `${revId}/compfail`;
        const chain: NodeId[] = unwindOrder.map((i) => unwEntry.get(i)!);
        if (!tailIsTerminator) {
          children.push({ kind: "terminator", nodeId: failNodeId, type: "fail", comp: { reversibleId: revId }, synthesized: true });
          chain.push(failNodeId);
        }
        // Per-step failure drops: forward item i fails → the unwind of the
        // greatest j < i that has one (the latest completed step's compensation).
        const itemsWithUnwind = [...unwEntry.keys()].sort((a, b) => a - b);
        const drops: { fromNodeId: NodeId; toNodeId: NodeId }[] = [];
        for (const [i, fromId] of fwdEntry) {
          let target: NodeId | undefined;
          for (const j of itemsWithUnwind) { if (j < i) target = unwEntry.get(j); else break; }
          if (target) drops.push({ fromNodeId: fromId, toNodeId: target });
        }
        if (chain.length) compensation.push({ reversibleId: revId, failNodeId, chain, drops });
        return children;
      }
      default:
        return [];
    }
  };

  const visitor: EmitVisitor = {
    step(u) {
      const mods = applyDist(topGroup().mods, u.node);
      const ws = buildLeafStep(u, mods, asByName, reentries);
      steps.push(ws);
      addNode(ws);
      // Map this step's walk NodeId to its IR NODE stepId key (hop segment
      // dropped — node ops target the whole call/note node).
      const uStepId = (u.node as { stepId?: { path: readonly string[] } }).stepId;
      if (uStepId) stepKeyByNodeId.set(ws.nodeId, uStepId.path.join("/"));
      // Resolve the owner-reassign target: only a CHAIN-HEAD hop (hopIndex 0)
      // with an authored caller span is safely rewritable. A later chain hop's
      // "caller" is the shared connector token (`B` in `A > B > C` is hop 0's
      // callee AND hop 1's caller) — rewriting it would corrupt the prior hop.
      if (u.kind === "call" && u.hopIndex === 0 && u.hop?.callerSource) {
        reassignTargets.set(u.stepIndex, u.hop.stepId.path.join("/"));
      }
      if (u.kind === "call" && u.isLastHop) {
        const an = (u.node as CallNode).asName?.name;
        if (an && !asByName.has(an)) asByName.set(an, ws);
      }
    },
    terminator(node, nodeId) {
      // An `unwind fail` lands here inside an unwind group, whose mods carry the
      // compensation flag + reversibleId — tag the terminator so the flow view
      // lifts it onto the backward run.
      const m = topGroup().mods;
      // Bridge the terminator's walk NodeId → its IR stepId key, so a stepId-
      // keyed mutation (inline reason edit) can target it (calls + blocks are
      // bridged elsewhere; terminators were the gap).
      const tStepId = (node as { stepId?: { path: readonly string[] } }).stepId;
      if (tStepId) stepKeyByNodeId.set(nodeId, tStepId.path.join("/"));
      addNode({
        kind: "terminator", nodeId, type: node.outcome,
        ...(node.reason ? { reason: node.reason.text } : {}),
        ...(node.all ? { all: true } : {}),
        ...(m.compensation && m.reversibleId ? { comp: { reversibleId: m.reversibleId } } : {}),
      });
    },
    open(c) {
      const parent = topGroup().mods;
      stack.push({ type: "container", c, groups: [], mods: applyDist(parent, c.node) });
    },
    close(c) {
      // Use the CLOSE event's container — it carries endIndex / expanded /
      // recursive (the open event had only startIndex). The popped frame
      // supplies the collected groups.
      const cf = stack.pop() as ContainerFrame;
      for (const n of buildContainer(c, cf.groups)) addNode(n);
    },
    group(g) {
      if (stack.length === 0) {
        stack.push({ type: "group", role: g.role, label: g.label, selected: g.selected, itemIndex: g.itemIndex, children: [], mods: DEFAULT_MODS });
        return;
      }
      const cf = stack[stack.length - 1] as ContainerFrame;
      stack.push({ type: "group", role: g.role, label: g.label, selected: g.selected, itemIndex: g.itemIndex, children: [], mods: groupDelta(cf.mods, cf.c, g) });
    },
    groupEnd() {
      const gf = stack.pop() as GroupFrame;
      if (stack.length === 0) {
        rootChildren = gf.children;
        return;
      }
      const cf = stack[stack.length - 1] as ContainerFrame;
      cf.groups.push({ role: gf.role, label: gf.label, selected: gf.selected, itemIndex: gf.itemIndex, children: gf.children });
    },
    diagnostic(d) {
      diagnostics.push({ kind: d.kind, nodeId: d.nodeId, target: d.target, chain: [...d.chain] });
    },
  };

  emitSteps(process.body, {
    // v0.9.0 §2.5: processes are splice targets too; spread order makes a
    // same-name subprocess win.
    subModels: ctx.processByName
      ? new Map<string, SubprocessModel | ProcessModel>([...ctx.processByName, ...ctx.subprocessByName])
      : ctx.subprocessByName,
    showAllBranches: opts.showAllBranches ?? false,
    branchSelection: state.branchSelection,
    expandedSubprocesses: state.expandedSubprocesses,
    rootId,
  }, visitor);

  // Forward pass: resolve `go`s whose `as` target appears LATER (the in-walk
  // backward resolution only saw already-walked steps).
  resolveReentries(steps, reentries);

  const byIndex = new Map<number, WalkStep>();
  for (const s of steps) byIndex.set(s.stepIndex, s);

  // v0.9.0 §2.1: leading `await` steps of the root flow are the process's
  // start trigger(s) — positional, so splice-expanded bodies never contribute.
  // The emitter's nodeId carries the IR kind (`p:X/await:0`) — keying on it
  // avoids false positives from a NOTE whose text happens to start "await ".
  const isAwaitStep = (n: WalkNode): boolean =>
    n.kind === "step" && isAwaitNodeId(n.nodeId);
  const triggers: NodeId[] = [];
  for (const n of rootChildren) {
    if (isAwaitStep(n)) {
      triggers.push((n as { nodeId: NodeId }).nodeId);
      continue;
    }
    // A multi-await head arrives as the emitter's §2.5 race demotion — a
    // leading parallel whose every branch is a single await step.
    if (
      n.kind === "parallel" &&
      triggers.length === 0 &&
      n.branches.length > 0 &&
      n.branches.every((b) => b.children.length === 1 && isAwaitStep(b.children[0]!))
    ) {
      for (const b of n.branches) triggers.push((b.children[0] as { nodeId: NodeId }).nodeId);
      break;
    }
    // Flow prose is not an action (§2.6) — it neither is a trigger nor ends
    // the leading-await run.
    if (n.kind === "step" && n.prose) continue;
    break;
  }

  return { steps, tree: rootChildren, byIndex, reassignTargets, stepKeyByNodeId, collapsedGroups, parallelGroups, diagnostics, reentries, compensation, triggers };
}

/** Short rendered merge threshold for a parallel merge gateway, or undefined for
 *  a bare `parallel { … }` (plain AND-join). `parallel select N` renders as
 *  `select N`. Reads the IR ParallelNode (the boolean merge text is already
 *  rendered into `threshold.text`). */
function parallelMergeAspect(n: ParallelNode): string | undefined {
  if (n.body.form === "select") {
    const c = n.body.count;
    const num = c.kind === "one" ? 1 : c.kind === "n" ? c.n : undefined;
    return num === undefined ? "select" : `select ${num}`;
  }
  const m = n.merge;
  const parts: string[] = [];
  if (m.detached) parts.push("out");
  if (m.policy) parts.push(m.policy);
  if (m.threshold) parts.push(m.threshold.kind === "boolean" ? m.threshold.text.text : String(m.threshold.n));
  // v0.9.0 §2.2: the boundary clause renders as its head (race is implied).
  if (n.awaitClause) parts.push(`await ${n.awaitClause.head.text}`);
  return parts.length ? parts.join(" ") : undefined;
}
